// Avatar único por hash del seed (teléfono o IG handle). Sin iniciales feas.
// Si hay `foto` (URL), la muestra. Si no, genera un SVG con gradiente y forma única.
// Pensado para CRM (inbox, ficha, mensajes), pero genérico para cualquier lado.

// Paletas armoniosas con la marca Limpack (navy + teal + variantes pastel)
const PALETAS = [
  ['#1A3461', '#00A8B5'], // navy + teal (marca)
  ['#0D9488', '#5BBDD8'], // verde-azulado
  ['#7C3AED', '#5BBDD8'], // violeta + sky
  ['#DC2626', '#F59E0B'], // rojo + ámbar (clientes "calientes")
  ['#16A34A', '#84CC16'], // verde + lima
  ['#EA580C', '#F59E0B'], // naranja
  ['#2563EB', '#5BBDD8'], // azul
  ['#DB2777', '#F472B6'], // rosa
  ['#92400E', '#FBBF24'], // marrón cálido
  ['#0E7490', '#67E8F9'], // cyan
  ['#7E22CE', '#C084FC'], // púrpura
  ['#0891B2', '#5BBDD8'], // azul cielo
]

// Hash simple y estable (djb2) → entero positivo
function hash(s) {
  let h = 5381
  const str = String(s || '')
  for (let i = 0; i < str.length; i++) h = ((h << 5) + h + str.charCodeAt(i)) | 0
  return Math.abs(h)
}

function paletaPara(seed) {
  return PALETAS[hash(seed) % PALETAS.length]
}

// Genera un SVG único (gradiente + 1-2 figuras geométricas suaves) basado en seed.
function svgGenerativo(seed, size) {
  const h = hash(seed)
  const [c1, c2] = paletaPara(seed)
  const angle = h % 360
  // Forma decorativa: círculo offset que se ve como onda de luz
  const off1x = ((h >> 3) % 50) - 10
  const off1y = ((h >> 5) % 50) - 10
  const r1 = 30 + ((h >> 7) % 40)
  const r2 = 18 + ((h >> 11) % 26)
  const off2x = ((h >> 13) % 70) + 30
  const off2y = ((h >> 17) % 70) + 30

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="${size}" height="${size}">
      <defs>
        <linearGradient id="g${h}" x1="0%" y1="0%" x2="100%" y2="100%" gradientTransform="rotate(${angle}, 0.5, 0.5)">
          <stop offset="0%" stop-color="${c1}" />
          <stop offset="100%" stop-color="${c2}" />
        </linearGradient>
      </defs>
      <rect width="100" height="100" fill="url(#g${h})" />
      <circle cx="${off1x}" cy="${off1y}" r="${r1}" fill="#ffffff" opacity="0.18" />
      <circle cx="${off2x}" cy="${off2y}" r="${r2}" fill="#ffffff" opacity="0.12" />
    </svg>`
  )
}

function svgToDataUrl(svg) {
  // No usar btoa con UTF-8 directo (rompe con caracteres no ASCII)
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`
}

/**
 * Avatar component.
 * - foto: URL pública. Si está, muestra <img>.
 * - seed: string para generar avatar único (teléfono, ig_username, nombre). Default: random.
 * - size: px. Default 36.
 * - badge: opcional, nodo a renderear en esquina inferior derecha (ej. canal icon).
 */
export default function Avatar({ foto, seed, size = 36, badge, className = '', style }) {
  const dim = { width: size, height: size, borderRadius: '50%', overflow: 'hidden', flexShrink: 0 }
  const wrap = { position: 'relative', display: 'inline-block', ...dim, ...style }
  if (foto) {
    return (
      <span className={className} style={wrap}>
        <img
          src={foto}
          alt=""
          style={{ width: size, height: size, objectFit: 'cover', display: 'block', borderRadius: '50%' }}
          onError={(e) => {
            // Si la foto rompe, fallback al generativo
            e.currentTarget.src = svgToDataUrl(svgGenerativo(seed || 'fallback', size))
          }}
        />
        {badge && <span style={badgeStyle(size)}>{badge}</span>}
      </span>
    )
  }
  const svg = svgGenerativo(seed || 'anon', size)
  return (
    <span className={className} style={wrap}>
      <span
        style={{ ...dim, display: 'block', background: `center/cover no-repeat url("${svgToDataUrl(svg)}")` }}
      />
      {badge && <span style={badgeStyle(size)}>{badge}</span>}
    </span>
  )
}

function badgeStyle(size) {
  // Badge más grande y prominente — para que se vea el canal de un vistazo
  const b = Math.max(16, Math.round(size * 0.5))
  return {
    position: 'absolute',
    bottom: -3,
    right: -3,
    width: b,
    height: b,
    borderRadius: '50%',
    background: '#fff',
    border: '2px solid #fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: Math.round(b * 0.58),
    lineHeight: 1,
    overflow: 'hidden',
    boxShadow: '0 2px 6px rgba(15, 27, 54, 0.18)',
  }
}
